import { db } from '$db/local/db';
import type { RecallItem, UUID } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso, notDeleted, endOfDayIso } from './common';

export interface CreateRecallItemInput {
  nodeId: UUID;
  originalAttemptId: UUID;
  originalProblemNumber: string;
  initialStability: number;
  initialDifficulty: number;
  nextReviewAt: string;
}

export async function createRecallItem(
  input: CreateRecallItemInput
): Promise<RecallItem> {
  const now = nowIso();
  const item: RecallItem = {
    id: newId(),
    userId: getActiveUserId(),
    nodeId: input.nodeId,
    originalAttemptId: input.originalAttemptId,
    originalProblemNumber: input.originalProblemNumber,
    stability: input.initialStability,
    difficulty: input.initialDifficulty,
    reps: 1,
    state: 'active',
    nextReviewAt: input.nextReviewAt,
    lastReviewAt: now,
    createdAt: now,
    updatedAt: now,
    deletedAt: null
  };
  await db.recallItems.add(item);
  return item;
}

export async function listActiveRecallItems(
  userId = getActiveUserId()
): Promise<RecallItem[]> {
  const rows = await db.recallItems.where('userId').equals(userId).toArray();
  return rows
    .filter((r) => r.state === 'active' && notDeleted(r))
    .sort((a, b) => a.nextReviewAt.localeCompare(b.nextReviewAt));
}

export async function listAllRecallItems(
  userId = getActiveUserId()
): Promise<RecallItem[]> {
  const rows = await db.recallItems.where('userId').equals(userId).toArray();
  return rows.filter(notDeleted);
}

export async function listDueRecallItems(
  cutoff: string = endOfDayIso(),
  userId = getActiveUserId()
): Promise<RecallItem[]> {
  const active = await listActiveRecallItems(userId);
  return active.filter((r) => r.nextReviewAt <= cutoff);
}

export async function getRecallItem(id: UUID): Promise<RecallItem | undefined> {
  return db.recallItems.get(id);
}

export interface UpdateRecallAfterReviewInput {
  stability: number;
  difficulty: number;
  reps: number;
  nextReviewAt: string;
  lastReviewAt: string;
  state?: RecallItem['state'];
}

export async function updateRecallAfterReview(
  id: UUID,
  input: UpdateRecallAfterReviewInput
): Promise<void> {
  await db.recallItems.update(id, {
    stability: input.stability,
    difficulty: input.difficulty,
    reps: input.reps,
    nextReviewAt: input.nextReviewAt,
    lastReviewAt: input.lastReviewAt,
    state: input.state ?? 'active',
    updatedAt: nowIso()
  });
}

export async function listRecallItemsByAttempt(
  originalAttemptId: UUID,
  userId = getActiveUserId()
): Promise<RecallItem[]> {
  const rows = await db.recallItems.where('userId').equals(userId).toArray();
  return rows.filter((r) => r.originalAttemptId === originalAttemptId && notDeleted(r));
}

export async function softDeleteRecallItem(id: UUID): Promise<void> {
  await db.recallItems.update(id, {
    deletedAt: nowIso(),
    updatedAt: nowIso()
  });
}